import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { 
  Code2, 
  Database, 
  Palette, 
  Smartphone, 
  Cloud, 
  Brain,
  Check,
  Lock,
  ChevronRight,
  Trophy
} from 'lucide-react'

const roadmaps = [
  {
    id: 'frontend',
    title: 'Frontend Developer',
    icon: Code2,
    color: '#5ed29c',
    description: 'Master modern web development with React, TypeScript, and more',
    duration: '6 months',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'HTML & CSS Fundamentals', duration: '2 weeks', completed: true },
      { title: 'JavaScript Deep Dive', duration: '4 weeks', completed: true },
      { title: 'React Fundamentals', duration: '4 weeks', completed: false },
      { title: 'TypeScript Mastery', duration: '3 weeks', completed: false },
      { title: 'Next.js & SSR', duration: '4 weeks', completed: false },
      { title: 'Testing & Performance', duration: '3 weeks', completed: false },
      { title: 'Portfolio Project', duration: '4 weeks', completed: false },
    ],
  },
  {
    id: 'backend',
    title: 'Backend Developer',
    icon: Database,
    color: '#4fd8ff',
    description: 'Build scalable APIs and services with Node.js, Python, and databases',
    duration: '8 months',
    level: 'Intermediate to Advanced',
    modules: [
      { title: 'Node.js Fundamentals', duration: '3 weeks', completed: false },
      { title: 'Express & REST APIs', duration: '3 weeks', completed: false },
      { title: 'Database Design', duration: '4 weeks', completed: false },
      { title: 'Authentication & Security', duration: '3 weeks', completed: false },
      { title: 'Microservices', duration: '4 weeks', completed: false },
      { title: 'DevOps Basics', duration: '3 weeks', completed: false },
      { title: 'System Design', duration: '4 weeks', completed: false },
    ],
  },
  {
    id: 'fullstack',
    title: 'Full-Stack Developer',
    icon: Cloud,
    color: '#8b5cf6',
    description: 'Become a complete developer with frontend and backend expertise',
    duration: '12 months',
    level: 'Beginner to Expert',
    modules: [
      { title: 'Web Fundamentals', duration: '4 weeks', completed: false },
      { title: 'Frontend with React', duration: '8 weeks', completed: false },
      { title: 'Backend with Node.js', duration: '8 weeks', completed: false },
      { title: 'Database Mastery', duration: '6 weeks', completed: false },
      { title: 'Cloud & Deployment', duration: '4 weeks', completed: false },
      { title: 'Real-world Projects', duration: '8 weeks', completed: false },
      { title: 'Interview Prep', duration: '4 weeks', completed: false },
    ],
  },
  {
    id: 'mobile',
    title: 'Mobile Developer',
    icon: Smartphone,
    color: '#5ed29c',
    description: 'Create cross-platform mobile apps with React Native and Flutter',
    duration: '6 months',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Mobile Development Basics', duration: '2 weeks', completed: false },
      { title: 'React Native Core', duration: '6 weeks', completed: false },
      { title: 'Flutter & Dart', duration: '6 weeks', completed: false },
      { title: 'State Management', duration: '3 weeks', completed: false },
      { title: 'Native Modules', duration: '3 weeks', completed: false },
      { title: 'App Store Deployment', duration: '2 weeks', completed: false },
    ],
  },
  {
    id: 'data',
    title: 'Data Scientist',
    icon: Brain,
    color: '#4fd8ff',
    description: 'Analyze data and build ML models with Python and modern tools',
    duration: '10 months',
    level: 'Intermediate to Expert',
    modules: [
      { title: 'Python for Data Science', duration: '4 weeks', completed: false },
      { title: 'Statistics & Probability', duration: '4 weeks', completed: false },
      { title: 'Data Visualization', duration: '3 weeks', completed: false },
      { title: 'Machine Learning', duration: '8 weeks', completed: false },
      { title: 'Deep Learning', duration: '6 weeks', completed: false },
      { title: 'MLOps', duration: '4 weeks', completed: false },
    ],
  },
  {
    id: 'uiux',
    title: 'UI/UX Designer',
    icon: Palette,
    color: '#8b5cf6',
    description: 'Design beautiful, user-centered interfaces and experiences',
    duration: '4 months',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Design Principles', duration: '2 weeks', completed: false },
      { title: 'Figma Mastery', duration: '4 weeks', completed: false },
      { title: 'User Research', duration: '3 weeks', completed: false },
      { title: 'Prototyping', duration: '3 weeks', completed: false },
      { title: 'Design Systems', duration: '4 weeks', completed: false },
      { title: 'Portfolio Building', duration: '4 weeks', completed: false },
    ],
  },
]

export function Roadmaps() {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, margin: '-100px' })
  const [activeRoadmap, setActiveRoadmap] = useState(roadmaps[0])

  return (
    <section
      ref={containerRef}
      id="roadmaps"
      className="relative py-32 overflow-hidden"
    >
      <div className="absolute inset-0 aurora-bg opacity-20" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Learning <span className="text-gradient">Roadmaps</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Structured paths to take you from beginner to job-ready professional
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Roadmap List */}
          <div className="space-y-4">
            {roadmaps.map((roadmap, index) => (
              <motion.button
                key={roadmap.id}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setActiveRoadmap(roadmap)}
                className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                  activeRoadmap.id === roadmap.id
                    ? 'glass-strong'
                    : 'glass hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${roadmap.color}20` }}
                  >
                    <roadmap.icon className="w-6 h-6" style={{ color: roadmap.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{roadmap.title}</h3>
                    <p className="text-sm text-white/50">{roadmap.duration}</p>
                  </div>
                  <ChevronRight 
                    className={`w-5 h-5 transition-transform ${
                      activeRoadmap.id === roadmap.id ? 'rotate-90 text-primary' : 'text-white/30'
                    }`} 
                  />
                </div>
              </motion.button>
            ))}
          </div>

          {/* Roadmap Details */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeRoadmap.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="glass rounded-2xl p-8"
              >
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <activeRoadmap.icon 
                        className="w-8 h-8" 
                        style={{ color: activeRoadmap.color }} 
                      />
                      <h3 className="text-2xl font-bold text-white">{activeRoadmap.title}</h3>
                    </div>
                    <p className="text-white/60">{activeRoadmap.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white/40">Level</div>
                    <div className="font-medium text-white">{activeRoadmap.level}</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white/60">Progress</span>
                    <span className="text-white font-medium">
                      {Math.round((activeRoadmap.modules.filter(m => m.completed).length / activeRoadmap.modules.length) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ backgroundColor: activeRoadmap.color }}
                      initial={{ width: 0 }}
                      animate={{ 
                        width: `${(activeRoadmap.modules.filter(m => m.completed).length / activeRoadmap.modules.length) * 100}%` 
                      }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Modules */}
                <div className="space-y-3">
                  {activeRoadmap.modules.map((module, index) => (
                    <motion.div
                      key={module.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                        module.completed ? 'bg-white/5' : 'hover:bg-white/5'
                      }`}
                    >
                      <div 
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          module.completed ? 'bg-primary/20' : 'bg-white/10'
                        }`}
                      >
                        {module.completed ? (
                          <Check className="w-4 h-4 text-primary" />
                        ) : (
                          <Lock className="w-4 h-4 text-white/40" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${module.completed ? 'text-white' : 'text-white/60'}`}>
                          {module.title}
                        </div>
                      </div>
                      <div className="text-sm text-white/40">{module.duration}</div>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white/60">
                    <Trophy className="w-5 h-5 text-primary" />
                    <span className="text-sm">Certificate on completion</span>
                  </div>
                  <motion.button
                    className="px-6 py-3 rounded-full font-medium text-background transition-all"
                    style={{ backgroundColor: activeRoadmap.color }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Start This Path
                  </motion.button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}